import {
  Breadcrumb,
  CabchargeImportFileModel,
  EntityDataItem,
  EntityHeader,
  FileImportStatus,
  JobImportFileModel,
  JobModel,
  LifecycleTransitionModel,
  LocationModel,
  NetworkConditionModel,
  OrganisationModel,
  TravellerModel,
  VehicleModel,
} from '@fleet/model';

import { DateTime } from 'luxon';
import { jobColorFromStatus } from './job-utilities';
import {
  locationsArrayFromJobDetail,
} from './location-utilities';
import { resolveVehicleBrandIcon } from './resolve-vehicle-brand-icon';
import { titleCaseClean } from './string-utilities';

// Color constants from job-colors.pipe.ts
const GREEN_COLOR = {
  textColor: 'text-green-700',
  bgColor: 'bg-green-50',
  badgeColor: 'green',
  textAndBgColor: 'bg-green-50 text-green-700',
};

const BLUE_COLOR = {
  textColor: 'text-blue-700',
  bgColor: 'bg-blue-50',
  badgeColor: 'blue',
  textAndBgColor: 'bg-blue-50 text-blue-700',
};

const YELLOW_COLOR = {
  textColor: 'text-yellow-700',
  bgColor: 'bg-yellow-50',
  badgeColor: 'yellow',
  textAndBgColor: 'bg-yellow-50 text-yellow-700',
};

const ORANGE_COLOR = {
  textColor: 'text-orange-700',
  bgColor: 'bg-orange-50',
  badgeColor: 'orange',
  textAndBgColor: 'bg-orange-50 text-orange-700',
};

const GRAY_COLOR = {
  textColor: 'text-gray-700',
  bgColor: 'bg-gray-50',
  badgeColor: 'gray',
  textAndBgColor: 'bg-gray-50 text-gray-700',
};

const RED_COLOR = {
  textColor: 'text-red-700',
  bgColor: 'bg-red-50',
  badgeColor: 'red',
  textAndBgColor: 'bg-red-50 text-red-700',
};

const PURPLE_COLOR = {
  textColor: 'text-purple-700',
  bgColor: 'bg-purple-50',
  badgeColor: 'purple',
  textAndBgColor: 'bg-purple-50 text-purple-700',
};

const PINK_COLOR = {
  textColor: 'text-pink-700',
  bgColor: 'bg-pink-50',
  badgeColor: 'pink',
  textAndBgColor: 'bg-pink-50 text-pink-700',
};

const INDIGO_COLOR = {
  textColor: 'text-indigo-700',
  bgColor: 'bg-indigo-50',
  badgeColor: 'indigo',
  textAndBgColor: 'bg-indigo-50 text-indigo-700',
};

const TEAL_COLOR = {
  textColor: 'text-teal-700',
  bgColor: 'bg-teal-50',
  badgeColor: 'teal',
  textAndBgColor: 'bg-teal-50 text-teal-700',
};

const DEFAULT_COLOR = {
  textColor: 'text-black',
  bgColor: 'bg-white',
  badgeColor: 'black',
  textAndBgColor: 'bg-white text-black',
};

const RED_600_COLOR = {
  textColor: 'text-red-600',
  bgColor: 'bg-red-50',
  badgeColor: 'red-600',
  textAndBgColor: 'bg-red-50 text-red-600',
};

const ORANGE_500_COLOR = {
  textColor: 'text-orange-500',
  bgColor: 'bg-orange-50',
  badgeColor: 'orange-500',
  textAndBgColor: 'bg-orange-50 text-orange-500',
};

const AMBER_500_COLOR = {
  textColor: 'text-amber-500',
  bgColor: 'bg-amber-50',
  badgeColor: 'amber-500',
  textAndBgColor: 'bg-amber-50 text-amber-500',
};

const EMERALD_500_COLOR = {
  textColor: 'text-emerald-500',
  bgColor: 'bg-emerald-50',
  badgeColor: 'emerald-500',
  textAndBgColor: 'bg-emerald-50 text-emerald-500',
};

const LIME_500_COLOR = {
  textColor: 'text-lime-500',
  bgColor: 'bg-lime-50',
  badgeColor: 'lime-500',
  textAndBgColor: 'bg-lime-50 text-lime-500',
};

const GREEN_500_COLOR = {
  textColor: 'text-green-500',
  bgColor: 'bg-green-50',
  badgeColor: 'green-500',
  textAndBgColor: 'bg-green-50 text-green-500',
};

const BLUE_500_COLOR = {
  textColor: 'text-blue-500',
  bgColor: 'bg-blue-50',
  badgeColor: 'blue-500',
  textAndBgColor: 'bg-blue-50 text-blue-500',
};

export function entityHeaderFromTraveller(
  traveller: TravellerModel,
  fleetProduct: string
) {
  let name = '';
  if (!traveller.firstName && !traveller.lastName) {
    name = 'Name Unknown';
  } else {
    name = traveller.firstName + ' ' + traveller.lastName;
    name = name.replace('undefined', '').trim();
  }

  const header = {
    title: name,

    dataItems: [
      {
        isChip: true,
        label: traveller.status,

        link: '/traveller/' + traveller.travellerId + '/settings/status',
      },
    ],
    avatar: {
      image: traveller.photo ? traveller.photo.url : null,
    },
  };

  if (traveller.phoneNumber) {
    if (fleetProduct === 'HUB') {
      header.dataItems.push({
        type: 'DIALABLE_PHONENUMBER',

        dialablePhoneNumber: {
          phoneNumber: traveller.phoneNumber,
          labelIcon: 'phone',
          tooltip: 'Call Traveller',
          phoneNumberContext: {
            travellerId: traveller.travellerId,
            name:
              traveller.firstName && traveller.lastName
                ? `${traveller.firstName} ${traveller.lastName}`
                    .replace('undefined', '')
                    .trim()
                : 'Name Unknown',
          },
        },
      } as any);
    } else {
      header.dataItems.push({
        label: traveller.phoneNumber,
        icon: 'phone',
      } as any);
    }
  }
  if (traveller.username) {
    header.dataItems.push({ label: traveller.username, icon: 'mail' } as any);
  }
  return header;
}

export function entityHeaderFromOrganisation(org: OrganisationModel) {
  const dataItems = [
    {
      label: org.organisationCode,
      icon: 'tag',
      toolTip: 'Organisation code',
    },

    {
      label: org.emailDomain,
      icon: 'mail',
      toolTip: 'Email',
    },
  ];

  if (org.taxIdentifiers && org.taxIdentifiers.length > 0) {
    org.taxIdentifiers.forEach((taxIdentifier) => {
      dataItems.push({
        label: taxIdentifier.identifier,
        toolTip: taxIdentifier.type,
        icon: 'business',
      });
    });
  }

  return {
    title: org.name,
    dataItems: dataItems,
    avatar: org.logoUrl
      ? {
          image: org.logoUrl,
          initials: org.name.substring(0, 1),
        }
      : null,
  };
}

export type VehicleHeaderPageType = 'SETTINGS' | 'DETAIL';

export interface VehicleEntityHeaderTooltips {
  status: string;
  makeModel: string;
  bodyType: string;
  manufactureYear: string;
}

export interface VehicleEntityHeaderContext {
  statusLink: string;
  /** When false, medallion is omitted from the header title. Omitted defaults to showing. */
  showMedallion?: boolean;
  tooltips: VehicleEntityHeaderTooltips;
}

export function vehicleEntityHeaderBreadcrumbs(params: {
  vehiclesLabel: string;
  settingsLabel: string;
  pageLabel: string;
  vehicleTitle: string;
  vehicleId: string;
  operatorId?: string;
  pageType: VehicleHeaderPageType;
}): Breadcrumb[] {
  const searchLink = params.operatorId
    ? '/operator/' + params.operatorId + '/vehicle/search'
    : '/vehicle/search';
  const breadcrumbs: Breadcrumb[] = [
    {
      label: params.vehiclesLabel,
      clickable: true,
      prefixArrow: false,
      link: searchLink,
    },
    {
      label: params.vehicleTitle,
      prefixArrow: true,
      clickable: params.pageType === 'SETTINGS',
      link: params.operatorId
        ? '/operator/' + params.operatorId + '/vehicle/' + params.vehicleId
        : '/vehicle/' + params.vehicleId,
    },
  ];
  if (params.pageType === 'SETTINGS') {
    breadcrumbs.push({
      label: params.settingsLabel,
      clickable: false,
      prefixArrow: true,
    });
  }
  breadcrumbs.push({
    label: params.pageLabel,
    prefixArrow: true,
    clickable: false,
  });
  return breadcrumbs;
}

export function entityHeaderFromVehicle(
  vehicle: VehicleModel,
  context: VehicleEntityHeaderContext
): Pick<EntityHeader, 'title' | 'dataItems' | 'avatar'> {
  const medallionTitle =
    context.showMedallion !== false && vehicle.medallion?.medallionNumber
      ? String(vehicle?.medallion?.type).trim().slice(0, 1) +
        vehicle?.medallion?.medallionNumber
      : '';
  const title = vehicle.plate
    ? vehicle?.plate?.plateNumber +
      (medallionTitle ? ' / ' + medallionTitle : '')
    : vehicle?.displayName;

  const dataItems = [
    {
      isChip: true,
      badgeStatus: vehicle.status,
      label: vehicle.status,
      toolTip: context.tooltips.status,
      link: context.statusLink,
    },
    {
      label: vehicle.make + '/' + vehicle.model,
      icon: 'directions_car',
      toolTip: context.tooltips.makeModel,
    },
  ] as EntityDataItem[];

  if (vehicle.bodyType) {
    dataItems.push({
      label: vehicle.bodyType,
      icon: 'badge',
      toolTip: context.tooltips.bodyType,
    });
  }
  if (vehicle.yearOfManufacture) {
    dataItems.push({
      label: vehicle.yearOfManufacture.toString(),
      icon: 'calendar_today',
      toolTip: context.tooltips.manufactureYear,
    });
  }

  return {
    title,
    dataItems,
    avatar: {
      image:
        vehicle.vehiclePhotoFront?.url ||
        resolveVehicleBrandIcon(vehicle.make) ||
        null,
      initials: `${(vehicle.make || '').slice(0, 1)}${(vehicle.model || '').slice(0, 1)}`.toUpperCase(),
    },
  };
}

export function orderLifecycleDescending(
  a: LifecycleTransitionModel,
  b: LifecycleTransitionModel
) {
  let first;
  let second;
  if (a.created && b.created) {
    first = DateTime.fromISO(a.created.toString());
    second = DateTime.fromISO(b.created.toString());
  } else {
    first = DateTime.fromISO(a.effectiveFrom.toString());
    second = DateTime.fromISO(b.effectiveFrom.toString());
  }

  return second.toMillis() - first.toMillis();
}

function locationRoleLabel(
  index: number,
  total: number,
  locationType?: string
): string {
  if (locationType === 'PICKUP' || index === 0) {
    return 'Pickup';
  }
  if (locationType === 'DROPOFF' || index === total - 1) {
    return 'Dropoff';
  }
  return `Waypoint ${index}`;
}

function locationHeaderTooltip(
  index: number,
  total: number,
  location: LocationModel
): string {
  return locationRoleLabel(index, total, location.type);
}

export function entityHeaderFromJob(
  job: JobModel,
  fleetProduct: string,
  showConditions?: boolean,
  pageLabel?: string,
  orgUrl?: string,
  linkJobId?: boolean
) {
  const dataItems: any[] = [
    {
      label: titleCaseClean(job.jobStatus),
      type: 'BADGE',
      badgeColor: jobColorFromStatus(job.jobStatus)?.badgeColor,
    },
    {
      label: job.jobId,
      toolTip: 'Job Reference',
      icon: 'tag',
      link: linkJobId ? '/job/' + job.jobId : null,
    },
  ];
  const waypointPlacesWithLetter = [];
  // const waypoints = placeModelArrayFromJobDetail(job.jobDetail);
  const waypointLocations = locationsArrayFromJobDetail(job.jobDetail);
  for (let i = 0; i < waypointLocations.length; i++) {
    if (waypointLocations[i]) {
      const loc = waypointLocations[i];
      const icon =
        loc.type === 'PICKUP' || i === 0
          ? 'trip_origin'
          : loc.type === 'DROPOFF' || i === waypointLocations.length - 1
            ? 'flag_circle'
            : 'radio_button_unchecked';
      waypointPlacesWithLetter.push({
        location: loc,
        icon,
      });
    }
  }
  waypointPlacesWithLetter.forEach((w, index) => {
    const toolTip = locationHeaderTooltip(
      index,
      waypointPlacesWithLetter.length,
      w.location
    );

    if (
      job.jobProgress &&
      job.jobProgress.activeLocation &&
      job.jobProgress.activeLocation.locationId === w.location.locationId
    ) {
      if (job.jobProgress.activeLocation.status === 'ARRIVED') {
        dataItems.push({
          label: w.location.displayLine1 + ' (ARRIVED)',
          icon: w.icon,
          toolTip,
        } as any);
      } else {
        dataItems.push({
          label:
            w.location.displayLine1 +
            ' (' +
            job.jobProgress.activeLocation.status +
            ' ' +
            job.jobProgress.displayEta +
            ')',
          icon: w.icon,
          toolTip,
        } as any);
      }
    } else {
      dataItems.push({
        label: w.location.displayLine1,
        icon: w.icon,
        toolTip,
      } as any);
    }
  });

  if (job.jobDetail.networkServiceDisplayName) {
    dataItems.push({
      label: job.jobDetail.networkServiceDisplayName,
      icon: 'local_taxi',
      toolTip: 'Service',
    });
  }

  if (job.driver) {
    if (job.driver.contactNumber && fleetProduct === 'HUB') {
      dataItems.push({
        label: job.driver.name,
        icon: 'badge',
        type: 'STANDARD',
        dialablePhoneNumber: {
          phoneNumber: job.driver.contactNumber,
          tooltip: 'Call Driver',
          phoneNumberContext: {
            driverId: job.driver.driverId,
            jobId: job.jobId,
            name: job.driver.name,
          },
        },
      });
    } else {
      dataItems.push({ label: job.driver.name, icon: 'badge' });
    }
  }
  if (job.vehicle?.medallion?.medallionNumber) {
    //Unable to do the PILL way for medallionNumber here, this does NOT return the type associated with it.
    //Cant do the paired colour from the TYPE letter
    dataItems.push({
      label: job.vehicle.medallion?.medallionNumber,
      icon: 'local_taxi',
      toolTip: 'Medallion Number',
    });
  }
  if (job.traveller) {
    if (job.traveller.contactNumber) {
      if (fleetProduct === 'HUB') {
        dataItems.push({
          label: job.traveller.name,
          icon: 'hail',
          type: 'STANDARD',
          dialablePhoneNumber: {
            phoneNumber: job.traveller.contactNumber,
            tooltip: 'Call Traveller',
            phoneNumberContext: {
              travellerId: job.traveller.travellerId,
              jobId: job.jobId,
              name: job.traveller.name,
            },
          },
        });
      } else {
        dataItems.push({ label: job.traveller.name, icon: 'hail' });
        dataItems.push({ label: job.traveller.contactNumber, icon: 'phone' });
      }
    } else {
      dataItems.push({ label: job.traveller.name, icon: 'hail' });
    }
  }
  if (job.jobDetail.conditions && job.jobDetail.conditions.length > 0) {
    job.jobDetail.conditions.forEach((condition: NetworkConditionModel) => {
      dataItems.push({
        icon: condition.iconName,
        label: condition.name,
        toolTip: condition.description,
      } as any);
    });
  }

  let title = waypointPlacesWithLetter[0]
    ? waypointPlacesWithLetter[0].location.displayLine1
    : 'No locations';

  // Check if this is a will call job (no start time but has will call date)
  if (!job.jobDetail.startTime && job.jobDetail.willCallDate) {
    const willCallDate = DateTime.fromISO(
      job.jobDetail.willCallDate.toString(),
      { setZone: true }
    );
    title =
      title + ' - Will Call: ' + willCallDate.toLocaleString(DateTime.DATE_MED);
  } else if (job.jobDetail.startTime) {
    title =
      title +
      ' - ' +
      DateTime.fromISO(job.jobDetail.startTime.toString(), {
        setZone: true,
      })
        .toLocaleString({
          ...DateTime.DATETIME_FULL,

          timeZoneName: 'long',
        } as any)
        .replace('GMT', '');
  }
  if (pageLabel) {
    //add breadcrumbs:
    const breadcrumbs: any[] = [
      {
        label: 'Jobs',
        clickable: true,
        prefixArrow: false,
        link: orgUrl + '/job/search',
      },
    ];
    waypointPlacesWithLetter[0]
      ? waypointPlacesWithLetter[0].location.displayLine1
      : 'No locations';

    breadcrumbs.push({
      label: waypointPlacesWithLetter[0]
        ? waypointPlacesWithLetter[0].location.displayLine1
        : 'Detail',

      prefixArrow: true,
      clickable: false,
    });

    breadcrumbs.push({
      label: pageLabel,
      prefixArrow: true,
      clickable: false,
    });
  }

  return {
    title: title,
    dataItems: dataItems,
  };
}

export function entityHeaderFromCabchargeImport(
  cabchargeImportFile: CabchargeImportFileModel,
  summary: any
) {
  const statusPill = entityStatusPill(cabchargeImportFile.fileImportStatus);
  const header = {
    title:
      'Cabcharge Import File' +
      (cabchargeImportFile?.document?.description
        ? ' - ' + cabchargeImportFile?.document?.description
        : ''),
    dataItems: [
      {
        isChip: true,
        textColor: statusPill.text,
        chipBgColor: statusPill.background,
        label: titleCaseClean(cabchargeImportFile.fileImportStatus),
        chipBgColorAndTextColor: statusPill.backgroundAndText,
      },
      {
        label: DateTime.fromISO(
          cabchargeImportFile.created.toString()
        ).toLocaleString(DateTime.DATETIME_FULL),

        icon: 'calendar_today',
        tooltip: 'Created',
      },
    ],
  };
  if (cabchargeImportFile.fileImportStatus === FileImportStatus.PROCESSED) {
    header.dataItems.push({
      label: summary.totalVerified + ' verified rows out of ' + summary.total,
      icon: 'verified',
    } as any);
    // header.dataItems.push({
    //   label: summary.totalValidationErrors + ' rows with validation errors '
    //   icon: 'verified',
    // } as any);
  }
  if (cabchargeImportFile.fileImportStatus === FileImportStatus.FAILED) {
    header.dataItems.push({
      label: summary.totalFailed + ' failed to import',
      icon: 'error',
    } as any);
    header.dataItems.push({
      label: summary.totalImported + ' adjustments imported',
      icon: 'check_circle',
    } as any);
  }
  if (cabchargeImportFile.fileImportStatus === FileImportStatus.IMPORTED) {
    header.dataItems.push({
      label: summary.totalImported + ' successfully imported',
      icon: 'check_circle',
    } as any);
  }

  return header;
}

export function entityHeaderFromJobImport(
  jobImportFile: JobImportFileModel,
  summary: any
) {
  const header = {
    title:
      'Job Import File' +
      (jobImportFile?.document?.description
        ? ' - ' + jobImportFile?.document?.description
        : ''),
    dataItems: [
      {
        isChip: true,
        label: jobImportFile.fileImportStatus,
      },
      {
        label: DateTime.fromISO(
          jobImportFile.created.toString()
        ).toLocaleString(DateTime.DATETIME_FULL),

        icon: 'calendar_today',
        tooltip: 'Created',
      },
    ],
  };
  if (jobImportFile.fileImportStatus === FileImportStatus.PROCESSED) {
    const totalDeleted = summary.totalDeleted || 0;
    const remainingTotal = summary.total - totalDeleted;
    header.dataItems.push({
      label: summary.totalVerified + ' verified rows out of ' + remainingTotal,
      icon: 'verified',
    } as any);
    if (totalDeleted > 0) {
      header.dataItems.push({
        label: totalDeleted + ' rows deleted',
        icon: 'delete',
      } as any);
    }
    // header.dataItems.push({
    //   label: summary.totalValidationErrors + ' rows with validation errors '
    //   icon: 'verified',
    // } as any);
  }
  if (jobImportFile.fileImportStatus === FileImportStatus.FAILED) {
    header.dataItems.push({
      label: summary.totalFailed + ' failed to import',
      icon: 'error',
    } as any);
    header.dataItems.push({
      label: summary.totalImported + ' jobs imported',
      icon: 'check_circle',
    } as any);
  }
  if (jobImportFile.fileImportStatus === FileImportStatus.IMPORTED) {
    header.dataItems.push({
      label: summary.totalImported + ' successfully imported',
      icon: 'check_circle',
    } as any);
  }

  return header;
}

export function entityStatusPill(status: string, inverse?: boolean): any {
  if (status) {
    switch (status) {
      case 'ACTIVE':
        return inverse
          ? {
              icon: 'done',
              background: 'bg-green-600',
              text: 'text-green-100',
              backgroundAndText: 'bg-green-600 text-green-100',
            }
          : {
              icon: 'done',
              background: 'bg-green-600',
              text: ' text-green-100',
              backgroundAndText: 'bg-green-600  text-green-100',
            };
      case 'VALID':
      case 'IMPORTED':
      case 'FINISHED':
      case 'APPROVED':
      case 'PAID':
      case 'PUBLISHED':
      case 'IN_PROGRESS':
      case 'STARTED':
      case 'SETTLED':
      case 'DISBURSED':
      case 'AVAILABLE':
      case 'COMPLETED':
      case 'DIRECT_DEBITED':
        return inverse
          ? {
              icon: 'done',
              background: 'bg-green-600',
              text: 'text-green-100',
              backgroundAndText: 'bg-green-700 text-green-100',
            }
          : {
              icon: 'done',
              background: 'bg-green-100',
              text: 'text-green-600',
              backgroundAndText: 'bg-green-100 text-green-700',
            };
      case 'BANNED':
      case 'INVALID':
      case 'FAILED':
      case 'BLOCKED':
      case 'SETTLEMENT_REJECTED':
      case 'CANCELLED':
      case 'REJECTED':
      case 'DECLINED':
      case 'DIRECT_DEBIT_REJECTED':
      case 'OVERDUE_RETURN':
        return inverse
          ? {
              background: 'bg-red-600',
              text: 'text-red-100',
              icon: 'block',
              backgroundAndText: 'bg-red-600 text-red-100',
            }
          : {
              background: 'bg-red-100',
              text: 'text-red-700',
              icon: 'block',
              backgroundAndText: 'bg-red-100 text-red-700',
            };
      case 'SUSPENDED':
      case 'VALIDATION_ERROR':
      case 'VERIFICATION_REQUIRED':
      case 'EXPIRED':
      case 'UNPUBLISHED':
      case 'ACKNOWLEDGEMENT_REQUIRED':
      case 'DISABLED':
      case 'LOST':
      case 'NO_REMITTANCE':
      case 'DISBURSEMENTS_DISABLED':
      case 'LATE_COLLECTION':
        return inverse
          ? {
              icon: 'block',
              background: 'bg-amber-500',
              text: 'text-amber-100',
              backgroundAndText: 'bg-amber-500 text-amber-100',
            }
          : {
              icon: 'block',
              background: 'bg-amber-100',
              text: 'text-amber-500',
              backgroundAndText: 'bg-amber-100 text-amber-500',
            };
      case 'PENDING_UNINSTALLATION':
      case 'OUTSTANDING':
        return inverse
          ? {
              icon: 'block',
              background: 'bg-pink-700',
              text: 'text-white',
              backgroundAndText: 'bg-pink-700 text-white',
            }
          : {
              icon: 'block',
              background: 'bg-pink-100',
              text: 'text-pink-400',
              backgroundAndText: 'bg-pink-100 text-pink-400',
            };
      case 'ON_LEAVE':
      case 'STICKY':
      case 'EXEMPTED':
      case 'RESERVED':
        return inverse
          ? {
              icon: 'cottage',
              background: 'bg-yellow-400',
              text: 'text-yellow-100',
              backgroundAndText: 'bg-yellow-400 text-yellow-100',
            }
          : {
              icon: 'cottage',
              background: 'bg-yellow-100',
              text: 'text-yellow-400',
              backgroundAndText: 'bg-yellow-100 text-yellow-400',
            };
      case 'INFO_REQUIRED':
        return inverse
          ? {
              icon: 'info',
              background: 'bg-gray-700',
              text: 'text-gray-300',
              backgroundAndText: 'bg-gray-700 text-gray-300',
            }
          : {
              icon: 'info',
              background: 'bg-gray-300',
              text: 'text-gray-700',
              backgroundAndText: 'bg-gray-300 text-gray-700',
            };
      case 'IN_ACTIVE':
      case 'OPEN':
      case 'INCOMPLETE':
      case 'ASSIGNED':
      case 'INACTIVE':
        return inverse
          ? {
              icon: 'edit_note',
              background: 'bg-gray-700',
              text: 'text-gray-300',
              backgroundAndText: 'bg-gray-700 text-gray-300',
            }
          : {
              icon: 'edit_note',
              background: 'bg-gray-300',
              text: 'text-gray-700',
              backgroundAndText: 'bg-gray-300 text-gray-700',
            };
      case 'RETURNED':
        return inverse
          ? {
              icon: 'info',
              textColor: 'text-gray-200',
              bgColor: 'bg-gray-500',
              badgeColor: 'gray-500',
              textAndBgColor: 'bg-gray-500 text-gray-200',
            }
          : {
              icon: 'info',
              textColor: 'text-gray-500',
              bgColor: 'bg-gray-50',
              badgeColor: 'gray-500',
              textAndBgColor: 'bg-gray-50 text-gray-500',
            };

      case 'VOID':
        return inverse
          ? {
              icon: 'block',
              background: 'bg-gray-700',
              text: 'text-gray-300',
              backgroundAndText: 'bg-gray-700 text-gray-300',
            }
          : {
              icon: 'block',
              background: 'bg-gray-300',
              text: 'text-gray-700',
              backgroundAndText: 'bg-gray-300 text-gray-700',
            };

      case 'DRAFT':
        return inverse
          ? {
              icon: 'pending',
              background: 'bg-gray-700',
              text: 'text-gray-300',
              backgroundAndText: 'bg-gray-700 text-gray-300',
            }
          : {
              icon: 'pending',
              background: 'bg-gray-300',
              text: 'text-gray-700',
              backgroundAndText: 'bg-gray-300 text-gray-700',
            };
      case 'DELETED':
      case 'WRITTEN_OFF':
        return inverse
          ? {
              icon: 'delete',
              background: 'bg-gray-700',
              text: 'text-gray-100',
              backgroundAndText: 'bg-gray-700 text-gray-100',
            }
          : {
              icon: 'delete',
              background: 'bg-gray-100',
              text: 'text-gray-700',
              backgroundAndText: 'bg-gray-100 text-gray-700',
            };
      case 'PROCESSED':
      case 'VALIDATED':
      case 'VERIFIED':
      case 'DISPATCHED':
      case 'COLLECTED':
        return inverse
          ? {
              icon: 'info',
              background: 'bg-blue-500',
              text: 'text-blue-100',
              backgroundAndText: 'bg-blue-500 text-blue-100',
            }
          : {
              icon: 'info',
              background: 'bg-blue-100',
              text: 'text-blue-500',
              backgroundAndText: 'bg-blue-100 text-blue-500',
            };

      case 'PENDING_REVIEW':
      case 'PENDING_INSTALLATION':
      case 'PENDING':
        return inverse
          ? {
              icon: 'schedule',
              background: 'bg-blue-500',
              text: 'text-blue-100',
              backgroundAndText: 'bg-blue-500 text-blue-100',
            }
          : {
              icon: 'schedule',
              background: 'bg-blue-100',
              text: 'text-blue-500',
              backgroundAndText: 'bg-blue-100 text-blue-500',
            };
      case 'INITIAL':
        return inverse
          ? {
              icon: 'info',
              background: 'bg-blue-700',
              text: 'text-blue-100',
              backgroundAndText: 'bg-blue-700 text-blue-100',
            }
          : {
              icon: 'info',
              background: 'bg-blue-100',
              text: 'text-blue-700',
              backgroundAndText: 'bg-blue-100 text-blue-700',
            };
      case 'OFFROAD':
      case 'PENDING_DEBT_COLLECTION':
      case 'ABANDONED':
        return inverse
          ? {
              icon: 'info',
              background: 'bg-orange-700',
              text: 'text-orange-100',
              backgroundAndText: 'bg-orange-700 text-orange-100',
            }
          : {
              icon: 'info',
              background: 'bg-orange-100',
              text: 'text-orange-700',
              backgroundAndText: 'bg-orange-100 text-orange-700',
            };
      case 'RESURRECTED':
      case 'PENDING_ACTIVATION':
        return inverse
          ? {
              icon: 'schedule',
              background: 'bg-green-500',
              text: 'text-green-100',
              backgroundAndText: 'bg-green-500 text-green-100',
            }
          : {
              icon: 'schedule',
              background: 'bg-green-100',
              text: 'text-green-500',
              backgroundAndText: 'bg-green-100 text-green-500',
            };
      case 'IN_REVIEW':
        return inverse
          ? {
              icon: 'rate_review',
              background: 'bg-orange-700',
              text: 'text-orange-300',
              backgroundAndText: 'bg-orange-700 text-orange-300',
            }
          : {
              icon: 'rate_review',
              background: 'bg-orange-300',
              text: 'text-orange-700',
              backgroundAndText: 'bg-orange-300 text-orange-700',
            };
      case 'PENDING_CLOSURE':
      case 'CLOSED':
        return inverse
          ? {
              icon: 'block',
              background: 'bg-black',
              text: 'text-white',
              backgroundAndText: 'bg-black text-white',
            }
          : {
              icon: 'block',
              background: 'bg-black',
              text: 'text-white',
              backgroundAndText: 'bg-black text-white',
            };
      default:
        return inverse
          ? {
              icon: 'info',
              background: 'bg-gray-700',
              text: 'text-gray-300',
              backgroundAndText: 'bg-gray-700 text-gray-300',
            }
          : {
              icon: 'info',
              background: 'bg-gray-300',
              text: 'text-gray-700',
              backgroundAndText: 'bg-gray-300 text-gray-700',
            };
    }
  }
  return null;
}

export function leaseStatusPill(
  status: string,
  options?: {
    inverse?: boolean;
    bgColorOnly?: boolean;
  }
): any {
  const leaseColors: Record<string, any> = {
    RESERVED: {
      textColor: 'text-gray-700',
      bgColor: 'bg-blue-200',
      badgeColor: 'blue-200',
      textAndBgColor: 'bg-blue-200 text-gray-700',
    },
    COLLECTED: {
      textColor: 'text-gray-700',
      bgColor: 'bg-green-200',
      badgeColor: 'green-200',
      textAndBgColor: 'bg-green-200 text-gray-700',
    },
    LATE_COLLECTION: {
      textColor: 'text-gray-700',
      bgColor: 'bg-yellow-200',
      badgeColor: 'yellow-200',
      textAndBgColor: 'bg-yellow-200 text-gray-700',
      tailBgColor: 'bg-orange-200',
    },
    OVERDUE_RETURN: {
      textColor: 'text-gray-800',
      bgColor: 'bg-red-200',
      badgeColor: 'red-200',
      textAndBgColor: 'bg-red-100 text-gray-800',
      tailBgColor: 'bg-red-300',
    },
    RETURNED: {
      textColor: 'text-gray-700',
      bgColor: 'bg-purple-200',
      badgeColor: 'purple-200',
      textAndBgColor: 'bg-purple-200 text-gray-700',
    },
    CANCELLED: {
      textColor: 'text-gray-700',
      bgColor: 'bg-gray-200',
      badgeColor: 'gray-200',
      textAndBgColor: 'bg-gray-200 text-gray-700',
    },
    /** Vehicle unavailable (not a lease); muted brown, same text weight as other lease pills */
    BLOCKOUT: {
      textColor: 'text-gray-700 dark:text-stone-100',
      bgColor: 'bg-[rgb(230_215_200)] dark:bg-[rgb(100_42_25)]',
      badgeColor: 'brown-200',
      textAndBgColor:
        'bg-[rgb(230_215_200)] text-gray-700 dark:bg-[rgb(100_42_25)] dark:text-stone-100',
    },
  };

  const normalizedStatus = (status || '').toString().toUpperCase();
  const result =
    leaseColors[normalizedStatus] ||
    ({
      textColor: 'text-white',
      bgColor: 'bg-gray-500',
      badgeColor: 'gray-500',
      textAndBgColor: 'bg-gray-500 text-white',
    } as any);

  if (options?.bgColorOnly) {
    return result.bgColor;
  }

  return result;
}

/**
 * BLOCKOUT row/calendar display metadata. Colours live on {@link leaseStatusPill}(`'BLOCKOUT'`).
 */
export function blockoutPill(): {
  colorClasses: string;
  displayText: string;
  icon: string;
} {
  const pill = leaseStatusPill('BLOCKOUT');
  return {
    colorClasses: pill.textAndBgColor,
    displayText: 'Blocked Out',
    icon: 'block',
  };
}
